"use client"

import { useState, useRef } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Toolbar,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Badge,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useTheme } from "@mui/material/styles"
import MapIcon from "@mui/icons-material/Map"
import ViewListIcon from "@mui/icons-material/ViewList"
import AddIcon from "@mui/icons-material/Add"
import TuneIcon from "@mui/icons-material/Tune"
import { useTranslation } from "../common/components/LocalizationProvider"
import { useDeviceReadonly } from "../common/util/permissions"
import DeviceRow from "./DeviceRow"

const useStyles = makeStyles()((theme) => ({
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
  },
  filterPanel: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    width: theme.dimensions.drawerWidthTablet,
    maxHeight: "70vh",
    overflowY: "auto",
  },
}))

const MainToolbar = ({
  filteredDevices,
  devicesOpen,
  setDevicesOpen,
  mapOnlyMode,
  setMapOnlyMode,
  keyword,
  setKeyword,
  filter,
  setFilter,
  filterSort,
  setFilterSort,
  filterMap,
  setFilterMap,
  searchFields,
  setSearchFields,
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const navigate = useNavigate()
  const t = useTranslation()

  const deviceReadonly = useDeviceReadonly()

  const groups = useSelector((state) => state.groups.items)
  const devices = useSelector((state) => state.devices.items)

  const toolbarRef = useRef()
  const inputRef = useRef()
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [devicesAnchorEl, setDevicesAnchorEl] = useState(null)

  const deviceStatusCount = (status) => Object.values(devices).filter((d) => d.status === status).length

  const hasActiveFilters =
    filter.statuses.length > 0 || filter.groups.length > 0 || searchFields.length !== 1 || !searchFields.includes("all")

  const getFieldLabel = (field) => {
    const fieldLabels = {
      all: t("sharedAll") || "Todos",
      name: t("sharedName") || "Nome",
      uniqueId: t("deviceIdentifier") || "IMEI/ID",
      phone: t("sharedPhone") || "Telefone",
      contact: t("sharedContact") || "Contato",
      model: t("deviceModel") || "Modelo",
      category: t("sharedCategory") || "Categoria",
      "attributes.operadora": "Operadora",
      "attributes.vendedor": "Vendedor",
      "attributes.iccid": "ICCID",
      "attributes.tecnico": "Técnico",
      "attributes.techphone": "Telefone Técnico",
      "attributes.equipmodel": "Modelo Equipamento",
      "attributes.datainstall": "Data Instalação",
      "attributes.consumo": "Consumo",
      "attributes.veiculo.placa": "Placa",
      "attributes.veiculo.renavan": "Renavan",
      "attributes.veiculo.chassi": "Chassi",
      "attributes.veiculo.ano": "Ano",
      "attributes.veiculo.cor": "Cor",
      "attributes.veiculo.marca": "Marca",
      "attributes.veiculo.modelo": "Modelo Veículo",
    }
    return fieldLabels[field] || field
  }

  return (
    <Toolbar ref={toolbarRef} className={classes.toolbar}>
      <IconButton
        edge="start"
        onClick={() => {
          const next = !devicesOpen
          setDevicesOpen(next)
          setMapOnlyMode(!next)
        }}
      >
        {mapOnlyMode ? <ViewListIcon /> : <MapIcon />}
      </IconButton>
      <OutlinedInput
        ref={inputRef}
        placeholder={t("sharedSearchDevices") || "Pesquisar Dispositivos"}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => setDevicesAnchorEl(toolbarRef.current)}
        onBlur={() => setDevicesAnchorEl(null)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton size="small" edge="end" onClick={() => setFilterAnchorEl(inputRef.current)}>
              <Badge color="info" variant="dot" invisible={!hasActiveFilters}>
                <TuneIcon fontSize="small" />
              </Badge>
            </IconButton>
          </InputAdornment>
        }
        size="small"
        fullWidth
      />
      <Popover
        open={!!devicesAnchorEl && !devicesOpen}
        anchorEl={devicesAnchorEl}
        onClose={() => setDevicesAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: Number(theme.spacing(2).slice(0, -2)),
        }}
        marginThreshold={0}
        slotProps={{
          paper: {
            style: { width: `calc(${toolbarRef.current?.clientWidth}px - ${theme.spacing(4)})` },
          },
        }}
        elevation={1}
        disableAutoFocus
        disableEnforceFocus
      >
        {filteredDevices.slice(0, 3).map((device, index) => (
          <DeviceRow key={device.id} data={filteredDevices} index={index} />
        ))}
        {filteredDevices.length > 3 && (
          <ListItemButton alignItems="center" onClick={() => setDevicesOpen(true)}>
            <ListItemText primary={t("notificationAlways") || "Ver Todos"} style={{ textAlign: "center" }} />
          </ListItemButton>
        )}
      </Popover>
      <Popover
        open={!!filterAnchorEl}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className={classes.filterPanel}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("sharedSearchIn") || "Buscar em"}</InputLabel>
            <Select
              label={t("sharedSearchIn") || "Buscar em"}
              value={searchFields}
              onChange={(e) => {
                const value = e.target.value
                if (value.includes("all")) {
                  setSearchFields(["all"])
                } else {
                  setSearchFields(value.filter((v) => v !== "all"))
                }
              }}
              multiple
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                    overflowY: "auto",
                  },
                },
              }}
              renderValue={(selected) => {
                if (selected.includes("all")) {
                  return t("sharedAll") || "Todos"
                }
                return selected.map((field) => getFieldLabel(field)).join(", ")
              }}
            >
              <MenuItem value="all">
                <Checkbox checked={searchFields.includes("all")} size="small" />
                <ListItemText primary={t("sharedAll") || "Todos"} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="name">
                <Checkbox checked={searchFields.includes("name")} size="small" />
                <ListItemText primary={t("sharedName") || "Nome"} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="uniqueId">
                <Checkbox checked={searchFields.includes("uniqueId")} size="small" />
                <ListItemText
                  primary={t("deviceIdentifier") || "IMEI/ID"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="phone">
                <Checkbox checked={searchFields.includes("phone")} size="small" />
                <ListItemText
                  primary={t("sharedPhone") || "Telefone"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="contact">
                <Checkbox checked={searchFields.includes("contact")} size="small" />
                <ListItemText
                  primary={t("sharedContact") || "Contato"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="model">
                <Checkbox checked={searchFields.includes("model")} size="small" />
                <ListItemText
                  primary={t("deviceModel") || "Modelo"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="attributes.veiculo.placa">
                <Checkbox checked={searchFields.includes("attributes.veiculo.placa")} size="small" />
                <ListItemText primary="Placa" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.renavan">
                <Checkbox checked={searchFields.includes("attributes.veiculo.renavan")} size="small" />
                <ListItemText primary="Renavan" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.chassi">
                <Checkbox checked={searchFields.includes("attributes.veiculo.chassi")} size="small" />
                <ListItemText primary="Chassi" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.ano">
                <Checkbox checked={searchFields.includes("attributes.veiculo.ano")} size="small" />
                <ListItemText primary="Ano" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.cor">
                <Checkbox checked={searchFields.includes("attributes.veiculo.cor")} size="small" />
                <ListItemText primary="Cor" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.marca">
                <Checkbox checked={searchFields.includes("attributes.veiculo.marca")} size="small" />
                <ListItemText primary="Marca" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.veiculo.modelo">
                <Checkbox checked={searchFields.includes("attributes.veiculo.modelo")} size="small" />
                <ListItemText primary="Modelo Veículo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.operadora">
                <Checkbox checked={searchFields.includes("attributes.operadora")} size="small" />
                <ListItemText primary="Operadora" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.vendedor">
                <Checkbox checked={searchFields.includes("attributes.vendedor")} size="small" />
                <ListItemText primary="Vendedor" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.iccid">
                <Checkbox checked={searchFields.includes("attributes.iccid")} size="small" />
                <ListItemText primary="ICCID" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.tecnico">
                <Checkbox checked={searchFields.includes("attributes.tecnico")} size="small" />
                <ListItemText primary="Técnico" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.techphone">
                <Checkbox checked={searchFields.includes("attributes.techphone")} size="small" />
                <ListItemText primary="Telefone Técnico" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.equipmodel">
                <Checkbox checked={searchFields.includes("attributes.equipmodel")} size="small" />
                <ListItemText primary="Modelo Equipamento" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.datainstall">
                <Checkbox checked={searchFields.includes("attributes.datainstall")} size="small" />
                <ListItemText primary="Data Instalação" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.consumo">
                <Checkbox checked={searchFields.includes("attributes.consumo")} size="small" />
                <ListItemText primary="Consumo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("deviceStatus") || "Status"}</InputLabel>
            <Select
              label={t("deviceStatus") || "Status"}
              value={filter.statuses}
              onChange={(e) => setFilter({ ...filter, statuses: e.target.value })}
              multiple
            >
              <MenuItem value="online">{`${t("deviceStatusOnline") || "Online"} (${deviceStatusCount("online")})`}</MenuItem>
              <MenuItem value="offline">{`${t("deviceStatusOffline") || "Offline"} (${deviceStatusCount("offline")})`}</MenuItem>
              <MenuItem value="unknown">{`${t("deviceStatusUnknown") || "Desconhecido"} (${deviceStatusCount("unknown")})`}</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("settingsGroups") || "Grupos"}</InputLabel>
            <Select
              label={t("settingsGroups") || "Grupos"}
              value={filter.groups}
              onChange={(e) => setFilter({ ...filter, groups: e.target.value })}
              multiple
            >
              {Object.values(groups)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("sharedSortBy") || "Ordenar por"}</InputLabel>
            <Select
              label={t("sharedSortBy") || "Ordenar por"}
              value={filterSort}
              onChange={(e) => setFilterSort(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">{"\u00a0"}</MenuItem>
              <MenuItem value="name">{t("sharedName") || "Nome"}</MenuItem>
              <MenuItem value="lastUpdate">{t("deviceLastUpdate") || "Última Atualização"}</MenuItem>
              <MenuItem value="attributes.veiculo.placa">Placa</MenuItem>
              <MenuItem value="attributes.operadora">Operadora</MenuItem>
              <MenuItem value="phone">Telefone</MenuItem>
              <MenuItem value="uniqueId">IMEI</MenuItem>
              <MenuItem value="attributes.veiculo.marca">Marca</MenuItem>
              <MenuItem value="attributes.veiculo.modelo">Modelo Veículo</MenuItem>
              <MenuItem value="attributes.tecnico">Técnico</MenuItem>
              <MenuItem value="attributes.vendedor">Vendedor</MenuItem>
              <MenuItem value="attributes.datainstall">Data Instalação</MenuItem>
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={filterMap} onChange={(e) => setFilterMap(e.target.checked)} />}
              label={t("sharedFilterMap") || "Filtrar Mapa"}
            />
          </FormGroup>
        </div>
      </Popover>
      <IconButton edge="end" onClick={() => navigate("/settings/device")} disabled={deviceReadonly}>
        <Tooltip
          open={!deviceReadonly && Object.keys(devices).length === 0}
          title={t("deviceRegisterFirst") || "Registre o primeiro dispositivo"}
          arrow
        >
          <AddIcon />
        </Tooltip>
      </IconButton>
    </Toolbar>
  )
}

export default MainToolbar